import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { createTeam, createTeamWithAccount, listTeams, getInvitations, acceptTeamInvitation, rejectTeamInvitation, deleteTeam } from '../api/teams';
import { Shield, Plus, Users, LayoutGrid, Check, X, LogIn, Key, RefreshCw, Settings, Trash2 } from 'lucide-react';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';

export default function Teams() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const view = queryParams.get('view') || 'teams';

  const [teams, setTeams] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Drawer states
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(view === 'create');
  
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamType, setTeamType] = useState('PERSONAL');
  const [creating, setCreating] = useState(false);
  
  // Dedicated Account states
  const [createAccount, setCreateAccount] = useState(false);
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountConfirm, setAccountConfirm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setIsCreateDrawerOpen(view === 'create');
  }, [view]);

  // Lock body scroll on mobile when drawer is open
  useEffect(() => {
    if (isCreateDrawerOpen) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [isCreateDrawerOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamsResult, invResult] = await Promise.allSettled([
        listTeams(),
        getInvitations(),
      ]);

      if (teamsResult.status === 'fulfilled') {
        setTeams(teamsResult.value || []);
      } else {
        console.error('Failed to load teams:', teamsResult.reason);
        setTeams([]);
      }

      if (invResult.status === 'fulfilled') {
        setInvitations(invResult.value || []);
      } else {
        console.error('Failed to load invitations:', invResult.reason);
        setInvitations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const activeTeams = useMemo(() => teams.filter((team) => team.status === 'ACTIVE'), [teams]);
  const totalMembers = useMemo(() => teams.reduce((count, team) => count + (team.total_members || 0), 0), [teams]);
  const sharedItems = useMemo(() => teams.reduce((count, team) => count + (team.shared_items || 0), 0), [teams]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;

    // Check for duplicate team name
    if (teams.some(t => t.name.trim().toLowerCase() === name.trim().toLowerCase())) {
      return setError('A team with this name already exists.');
    }
    
    if (createAccount) {
      if (!accountEmail.trim() || !accountPassword) {
        return setError('Email and password are required for a team account');
      }
      if (accountPassword !== accountConfirm) {
        return setError('Passwords do not match');
      }
    }

    setError('');
    setCreating(true);
    try {
      if (createAccount) {
        const vaultCrypto = new VaultCrypto(WebCryptoProvider);
        const authKey = await vaultCrypto.generateAuthKey(accountPassword, accountEmail);
        const mek = await vaultCrypto.generateMEK(accountPassword, accountEmail);
        const vk = vaultCrypto.generateVaultKey();
        const encryptedVaultKey = await vaultCrypto.wrapVaultKey(vk, mek);
        
        await createTeamWithAccount({
          teamName: name,
          description,
          teamType,
          email: accountEmail,
          masterPasswordHash: authKey,
          encryptedVaultKey
        });
      } else {
        await createTeam({ name, description, teamType });
      }
      
      setName('');
      setDescription('');
      setCreateAccount(false);
      setAccountEmail('');
      setAccountPassword('');
      setAccountConfirm('');
      setIsCreateDrawerOpen(false);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleAcceptInvite = async (invitationId) => {
    try {
      await acceptTeamInvitation(invitationId);
      await fetchData();
    } catch (error) {
      console.error('Failed to accept', error);
    }
  };

  const handleRejectInvite = async (invitationId) => {
    try {
      await rejectTeamInvitation(invitationId);
      await fetchData();
    } catch (error) {
      console.error('Failed to reject', error);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to delete the team "${teamName}"? This action cannot be undone.`)) {
      try {
        await deleteTeam(teamId);
        await fetchData();
      } catch (err) {
        console.error('Failed to delete team', err);
        alert(err.response?.data?.message || 'Failed to delete team');
      }
    }
  };

  return (
    <MainLayout>
      <div className="page-header teams-page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="page-title notes-hero-title" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
              <Users size={24} />
              Teams
            </h1>
            <p className="notes-hero-subtitle" style={{ marginTop: '4px' }}>Create teams, invite users, and manage secure shared access.</p>
          </div>
          <div className="teams-header-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button onClick={fetchData} variant="secondary" className="notes-hero-btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button onClick={() => navigate('/teams?view=create')} className="notes-hero-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Plus size={14} /> Create Team
            </Button>
          </div>
        </div>
      </div>

      <div>
        {/* Summary Cards */}
        <div className="teams-summary-grid" style={{ marginBottom: '24px' }}>
          <div className="glass-panel teams-summary-card">
            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Teams</div>
            <div style={{ fontSize: '24px', fontWeight: '600' }}>{teams.length}</div>
          </div>
          <div className="glass-panel teams-summary-card">
            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Teams</div>
            <div style={{ fontSize: '24px', fontWeight: '600' }}>{activeTeams.length}</div>
          </div>
          <div className="glass-panel teams-summary-card">
            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Members</div>
            <div style={{ fontSize: '24px', fontWeight: '600' }}>{totalMembers}</div>
          </div>
          <div className="glass-panel teams-summary-card">
            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Shared Items</div>
            <div style={{ fontSize: '24px', fontWeight: '600' }}>{sharedItems}</div>
          </div>
          <div className="glass-panel teams-summary-card">
            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invitations</div>
            <div style={{ fontSize: '24px', fontWeight: '600', color: invitations.length > 0 ? 'var(--accent-primary)' : 'inherit' }}>{invitations.length}</div>
          </div>
        </div>

        {/* View specific content */}
        {view === 'invitations' ? (
          <div className="glass-panel" style={{ padding: '20px', borderRadius: '12px' }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.05rem' }}>Pending Invitations</h2>
            {invitations.length === 0 ? (
              <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.84rem' }}>
                No pending invitations.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {invitations.map(inv => (
                  <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '0.9rem', marginBottom: '2px' }}>{inv.teamName}</div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Invited as <strong>{inv.role}</strong> • Expires {new Date(inv.expiresAt).toLocaleDateString()}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button variant="danger" onClick={() => handleRejectInvite(inv.id)} style={{ padding: '6px 12px', fontSize: '12px' }}><X size={14} /></Button>
                      <Button variant="primary" onClick={() => handleAcceptInvite(inv.id)} style={{ padding: '6px 12px', fontSize: '12px' }}><Check size={14} /> Accept</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Your Teams</h2>
            </div>
            
            {!loading && teams.length === 0 ? (
              <div className="glass-panel" style={{ padding: '48px 20px', textAlign: 'center', borderRadius: '12px' }}>
                <Shield size={40} color="var(--accent-primary)" style={{ margin: '0 auto 12px', opacity: 0.8 }} />
                <h3 style={{ fontSize: '1.05rem', marginBottom: '6px' }}>You're not part of any team yet.</h3>
                <p style={{ color: 'var(--text-secondary)', maxWidth: '380px', margin: '0 auto 20px', fontSize: '0.78rem' }}>
                  Create a secure team to share encrypted vault items with trusted members.
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button onClick={() => navigate('/teams?view=create')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Plus size={14} /> Create Team</Button>
                  <Button variant="secondary" onClick={() => navigate('/teams?view=invitations')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><LogIn size={14} /> Join With Invitation</Button>
                </div>
              </div>
            ) : (
              <div className="teams-cards-grid">
                {teams.map((team) => (
                  <div key={team.id} className="glass-panel team-item-card" style={{ padding: '16px', borderRadius: '12px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: '0.94rem' }}>{team.name}</h3>
                        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {team.description || 'No description provided'}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', gap: '12px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '2px' }}>ROLE</div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{team.memberRole}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '2px' }}>MEMBERS</div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{team.total_members || 0}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '2px' }}>SHARED</div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{team.shared_items || 0}</div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <Button onClick={() => navigate(`/teams/${team.id}`)} style={{ flex: 1, textAlign: 'center', fontSize: '0.78rem', padding: '6px 10px' }}>Open Team</Button>
                        <Button variant="secondary" onClick={() => navigate(`/teams/${team.id}?tab=settings`)} style={{ padding: '0 10px' }}>
                          <Settings size={15} />
                        </Button>
                        {team.memberRole === 'OWNER' && (
                          <Button variant="danger" onClick={() => handleDeleteTeam(team.id, team.name)} style={{ padding: '0 10px' }}>
                            <Trash2 size={15} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right-side Drawer for Create Team */}
      {isCreateDrawerOpen && (
        <div className="create-team-drawer">
          <div className="create-team-drawer-header">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Create Team</h2>
            <button onClick={() => navigate('/teams')} aria-label="Close drawer" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <X size={18} />
            </button>
          </div>
          <div className="create-team-drawer-body">
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '16px', minHeight: '100%' }}>
              {error && <div style={{ color: 'var(--danger)', padding: '10px 12px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', fontSize: '13px' }}>{error}</div>}
              
              <Input label="Company / Team Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Security Engineering" required />
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>Description</label>
                <textarea 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  placeholder="Private vault collaboration space"
                  style={{ 
                    padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', 
                    background: 'var(--bg-secondary)', color: 'var(--text-primary)', 
                    fontFamily: 'inherit', resize: 'vertical', minHeight: '75px',
                    boxSizing: 'border-box', width: '100%', fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>Team Type</label>
                <select 
                  value={teamType} 
                  onChange={(e) => setTeamType(e.target.value)}
                  style={{ 
                    padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', 
                    background: 'var(--bg-secondary)', color: 'var(--text-primary)', 
                    fontFamily: 'inherit', boxSizing: 'border-box', width: '100%', fontSize: '14px',
                  }}
                >
                  <option value="PERSONAL">Personal Team</option>
                  <option value="BUSINESS">Business Team</option>
                </select>
              </div>

              <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0', paddingTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '14px' }}>
                  <input 
                    type="checkbox" 
                    checked={createAccount} 
                    onChange={(e) => setCreateAccount(e.target.checked)} 
                    style={{ width: '18px', height: '18px', accentColor: 'var(--accent-primary)' }}
                  />
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>Create dedicated team login</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Creates an account that only has access to this team's resources.</div>
                  </div>
                </label>

                {createAccount && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '2px' }}>
                      <Key size={14} /> Master password will encrypt this team's root keys
                    </div>
                    <Input label="Login Email" type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} required={createAccount} />
                    <Input label="Master Password" type="password" value={accountPassword} onChange={(e) => setAccountPassword(e.target.value)} required={createAccount} />
                    <Input label="Confirm Password" type="password" value={accountConfirm} onChange={(e) => setAccountConfirm(e.target.value)} required={createAccount} />
                  </div>
                )}
              </div>

              <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                <Button type="submit" loading={creating} style={{ width: '100%' }}>Create Team</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Drawer Overlay */}
      {isCreateDrawerOpen && (
        <div 
          onClick={() => navigate('/teams')}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', zIndex: 99
          }}
        />
      )}
    </MainLayout>
  );
}
